class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.headerStates = {
      isFixed: false,
      isHidden: false,
      isTransparent: false,
      isMegaMenuOpen: false,
      isSearchOpen: false,
    };
  }

  connectedCallback() {
    // Cache DOM elements
    this.cacheElements();

    // Initialize header state
    this.initializeHeader();

    // Set up event listeners
    this.setupEventListeners();

    // Create observer for header bounds
    this.createObserver();
  }

  /**
   * Cache commonly accessed DOM elements
   */
  cacheElements() {
    this.header = document.querySelector('.section-header');
    this.headerWrapper = document.querySelector('.header-wrapper');
    this.megaMenuTriggers = document.querySelectorAll('.header__menu-item[aria-expanded]');
    this.megaMenuContainer = document.querySelector('.header__inline-menu');
    this.searchModal = this.header.querySelector('details-modal');
    this.filterBar = document.querySelector('.ais-search-header');

    // Get header configuration
    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size';
  }

  /**
   * Initialize header state and styles
   */
  initializeHeader() {
    // Set initial header position
    this.headerTop = this.headerWrapper.offsetTop;

    // Add initial transparency class if at top
    if (window.scrollY === 0) {
      this.setHeaderState('isTransparent', true);
    }

    // Set header height
    this.setHeaderHeight();

    // Initialize always sticky if configured
    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    // Initialize scroll state
    this.currentScrollTop = 0;
    this.preventReveal = false;
  }

  /**
   * Set up all event listeners
   */
  setupEventListeners() {
    // Scroll handling
    this.onScrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, false);

    // Responsive header height
    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    // Prevent header reveal event
    this.addEventListener('preventHeaderReveal', () => (this.preventReveal = true));

    // Mega menu handlers
    this.setupMegaMenuHandlers();

    // Search modal handlers
    if (this.searchModal) {
      this.setupSearchHandlers();
    }

    // Algolia integration
    if (this.filterBar) {
      this.setupAlgoliaHandlers();
    }
  }

  /**
   * Set header state and update classes
   */
  setHeaderState(state, value) {
    this.headerStates[state] = value;

    // Update classes based on state
    switch (state) {
      case 'isTransparent':
        this.header.classList.toggle('header--transparent', value);
        break;
      case 'isHidden':
        this.headerWrapper.classList.toggle('is-hidden', value);
        break;
      case 'isFixed':
        this.headerWrapper.classList.toggle('is-fixed', value);
        break;
    }
  }

  /**
   * Handle scroll events
   */
  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const isProductTemplate = document.body.classList.contains('template--product');
    // const isAlgoliaTemplate = document.body.classList.contains('template--collection');

    // Update transparency
    this.setHeaderState('isTransparent', scrollTop <= 110);

    // Handle scroll to top
    if (scrollTop === 0) {
      this.headerWrapper.classList.remove('is-hidden', 'is-fixed', 'animate-up', 'animate-down');

      if (document.querySelector('sticky-header').classList.contains('header-wrapper--homepage')) {
        this.headerWrapper.classList.add('transparent');
      }

      return;
    }

    // Product/Algolia template specific behavior
    // if (isProductTemplate || isAlgoliaTemplate) {
    if (!isProductTemplate) {
      this.handleDefaultScroll(scrollTop);
    }

    this.currentScrollTop = scrollTop;
  }

  /**
   * Handle scroll behavior for default templates
   * @param {number} scrollTop - Current scroll position
   */
  handleDefaultScroll(scrollTop) {
    // Don't process if menu/search is open
    if (this.headerStates.isMegaMenuOpen || this.headerStates.isSearchOpen) {
      return;
    }

    // Constants for thresholds
    const SCROLL_THRESHOLD = 0; // Show/hide header after this many pixels
    // const TOP_THRESHOLD = 42; // Consider at top within this many pixels
    const TOP_THRESHOLD = 106; // Consider at top within this many pixels
    const isAtTop = scrollTop <= TOP_THRESHOLD;
    const isScrollingDown = scrollTop > this.currentScrollTop;
    const hasScrolledPastThreshold = scrollTop > SCROLL_THRESHOLD;

    requestAnimationFrame(() => {
      // At the top - reset to default state
      if (isAtTop) {
        this.headerWrapper.classList.remove('is-hidden', 'is-fixed');
        if (document.querySelector('sticky-header').classList.contains('header-wrapper--homepage')) {
          this.headerWrapper.classList.add('transparent');
        }
        return;
      }

      // Past threshold - handle sticky behavior
      if (hasScrolledPastThreshold) {
        if (scrollTop >= 104) {
          this.headerWrapper.classList.add('is-fixed');

          // Toggle visibility based on scroll direction
          if (isScrollingDown && !this.preventReveal) {
            this.headerWrapper.classList.add('is-hidden');
          } else {
            this.headerWrapper.classList.remove('is-hidden');
          }
        }

        if (document.querySelector('sticky-header').classList.contains('header-wrapper--homepage')) {
          this.headerWrapper.classList.remove('transparent');
        }
      } else {
        // Between top and threshold - transitional state
        this.headerWrapper.classList.remove('is-hidden');

        if (document.querySelector('sticky-header').classList.contains('header-wrapper--homepage')) {
          this.headerWrapper.classList.add('transparent');
        }
      }
    });

    // Update scroll position
    this.currentScrollTop = scrollTop;
  }

  setupMegaMenuHandlers() {
    const megaMenuTriggers = document.querySelectorAll('.header__menu-item[aria-expanded]');
    const megaMenuContainer = document.querySelector('.header__inline-menu');

    megaMenuTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        // Close all other mega menus first
        megaMenuTriggers.forEach((otherTrigger) => {
          if (otherTrigger !== trigger) {
            otherTrigger.setAttribute('aria-expanded', 'false');
            const otherContent = document.getElementById(otherTrigger.getAttribute('aria-controls'));
            if (otherContent) {
              otherContent.classList.remove('mega-menu--active');
            }
          }
        });

        // Toggle current mega menu
        trigger.setAttribute('aria-expanded', (!isExpanded).toString());
        const content = document.getElementById(trigger.getAttribute('aria-controls'));
        if (content) {
          content.classList.toggle('mega-menu--active');
        }
      });
    });

    // Close mega menu only when clicking outside the entire menu area
    document.addEventListener('click', (e) => {
      const target = e.target;
      const megaMenuContent = target.closest('.mega-menu__content');
      // Don't close if the click is within the mega menu container or any mega menu content
      if (megaMenuContainer && !megaMenuContainer.contains(target) && !megaMenuContent) {
        megaMenuTriggers.forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
          const content = document.getElementById(trigger.getAttribute('aria-controls'));
          if (content) {
            content.classList.remove('mega-menu--active');
          }
        });
      }
    });
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', () => (this.preventReveal = true));
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    const observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;

    if (this.header.querySelector('.product-header') && window.innerWidth >= 991) {
      this.header.classList.remove('shopify-section-header-sticky', 'animate', 'shopify-section-header-hidden');
    } else {
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    }
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    const isCompareNavSticky = document.querySelectorAll('.ais-search-header.sticky.shadow').length > 0;
    if (this.headerIsAlwaysSticky || isCompareNavSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    const isCompareNavSticky = document.querySelectorAll('.ais-search-header.sticky.shadow').length > 0;

    if (this.headerIsAlwaysSticky || isCompareNavSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    // this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    // this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    this.searchModal.close(false);
  }

  /**
   * Set up search modal functionality and interactions
   */
  setupSearchHandlers() {
    if (!this.searchModal) return;

    // Track search modal state
    const searchDetails = this.searchModal.querySelector('details');

    if (!searchDetails) return;

    // Handle search open/close
    searchDetails.addEventListener('toggle', () => {
      const isOpen = searchDetails.hasAttribute('open');
      this.setHeaderState('isSearchOpen', isOpen);

      if (isOpen) {
        // When search opens
        this.headerWrapper.classList.remove('is-hidden', 'animate-down');

        document.body.classList.add('overflow-hidden');
        /* Add overflow-hidden to html element */
        document.documentElement.classList.add('overflow-hidden');

        // Close mega menu if open
        if (this.headerStates.isMegaMenuOpen) {
          this.megaMenuTriggers.forEach((trigger) => {
            trigger.setAttribute('aria-expanded', 'false');
            const content = document.getElementById(trigger.getAttribute('aria-controls'));
            if (content) {
              content.classList.remove('mega-menu--active');
            }
          });
          this.setHeaderState('isMegaMenuOpen', false);
        }

        const searchInput = this.searchModal.querySelector('input[type="search"]');

        if (searchInput) {
          setTimeout(() => {
            searchInput.focus();
          }, 150);
        }
        this.setHeaderState('isFixed', true);
        const headerHeight = this.headerWrapper.getBoundingClientRect().height;

        const searchModalInHeader = this.querySelector('.search-modal__content');
        const searchModalInHeaderDimensions = searchModalInHeader.getBoundingClientRect();
        const searchModalInHeaderBottom = searchModalInHeaderDimensions.bottom;
        /* get current scroll position */
        const currentScrollPosition = window.scrollY;

        const algoliaPanel = document.querySelector('.aa-Panel');

        const topOverride = searchModalInHeaderBottom;

        if (window.matchMedia('(max-width: 990px)').matches) return;
        const panelInterval = setInterval(() => {
          if (document.querySelector('.aa-Panel')) {
            document.querySelector('.aa-Panel').style.top = `${topOverride}px`;
            clearInterval(panelInterval);
          }
        }, 10);
      } else {
        // When search closes
        document.body.classList.remove('overflow-hidden');
        document.documentElement.classList.remove('overflow-hidden');
      }
    });

    // Handle close button click
    const closeButton = this.searchModal.querySelector('.search-modal__close-button');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.setHeaderState('isSearchOpen', false);
        searchDetails.removeAttribute('open');
      });
    }

    // Handle clear button click
    const clearButton = this.searchModal.querySelector('.search-modal__clear-button');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        const searchInput = this.searchModal.querySelector('input[type="search"]');
        if (searchInput) {
          searchInput.value = '';
          searchInput.focus();
        }
      });
    }

    // Handle overlay click
    const modalOverlay = this.searchModal.querySelector('.modal-overlay');
    if (modalOverlay) {
      modalOverlay.addEventListener('click', () => {
        this.setHeaderState('isSearchOpen', false);
        searchDetails.removeAttribute('open');
      });
    }

    // Handle keyboard interactions
    this.searchModal.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') {
        this.setHeaderState('isSearchOpen', false);
        searchDetails.removeAttribute('open');
      }
    });

    // Prevent search modal from closing when clicking inside
    this.searchModal.addEventListener('click', (event) => {
      if (event.target.closest('.search-modal__content')) {
        event.stopPropagation();
      }
    });

    // Handle predictive search results
    if (window.Shopify?.theme?.predictiveSearch) {
      const searchForm = this.searchModal.querySelector('predictive-search');
      if (searchForm) {
        searchForm.addEventListener('predictive-search-results', () => {
          // Ensure header stays visible when results are shown
          this.headerWrapper.classList.remove('is-hidden');
          this.setHeaderState('isFixed', true);
        });
      }
    }
  }
}

customElements.define('sticky-header', StickyHeader);
